import React, { useEffect, useMemo, useRef, useState } from "react";
import ModalOrSheet from "./ModalOrSheet.jsx";
import EmailPreviewBody from "./previews/EmailPreviewBody.jsx";
import TextPreviewBody from "./previews/TextPreviewBody.jsx";
import UnsupportedPreviewPanel from "./previews/UnsupportedPreviewPanel.jsx";
import PdfInlinePreview from "./previews/PdfInlinePreview.jsx";
import AppButton from "./AppButton.jsx";
import {
  getFileExtension,
  getFileType,
  getPreviewStrategy,
  isImageFile,
  isPdfFile,
  MAX_TEXT_PREVIEW_BYTES,
  sanitizeFileUrl,
  shouldUsePreviewMeta,
} from "../../utils/filePreview.js";
import { preferPdfExportActionSheet } from "../../utils/pdfExportUi.js";
import {
  fetchAuthenticatedPreviewText,
  revokeAuthenticatedPreviewUrl,
} from "../../utils/authenticatedFilePreview.js";
import { fetchProtectedPreviewAssets, isExportPdfUrl, PdfPreviewError } from "../../utils/exportPdfPreview.js";
import { buildPreviewMetaUrl, fetchPreviewMeta } from "../../utils/previewMeta.js";
import {
  downloadProtectedFile,
  openProtectedFileInNewTab,
  printProtectedFile,
} from "../../utils/openProtectedFile.js";
import {
  shouldOfferNativeFileShare,
  shareProtectedFile,
} from "../../utils/nativeFileShare.js";
import { showError, showSuccess, showInfo } from "../../utils/toast.js";
import api from "../../constants/api.js";
import { useAuthOptional } from "./AuthContext.jsx";

function normalizeType(type = "") {
  const raw = String(type || "").trim().toLowerCase();
  if (!raw) return "";
  return raw.startsWith(".") ? raw.slice(1) : raw;
}

function formatMaxPreviewSize(bytes = MAX_TEXT_PREVIEW_BYTES) {
  const kb = Math.round(bytes / 1024);
  return kb >= 1024 ? `${(kb / 1024).toFixed(0)} MB` : `${kb} KB`;
}

export default function FilePreviewModal({
  open,
  onClose,
  fileUrl,
  fileName = "",
  fileType = "",
  downloadUrl = "",
  title = "",
  description = "",
  canDownload = true,
  canOpenInNewTab = true,
  canPrint = true,
  canShare = true,
  previewModule = "",
  fileId = null,
  projektTyp = "",
  projektId = null,
  entityId = null,
}) {
  const auth = useAuthOptional();
  const sharepointReady = Boolean(auth?.sharepointReady);
  const [imgFailed, setImgFailed] = useState(false);
  const [pdfFailed, setPdfFailed] = useState(false);
  const [resolvedUrl, setResolvedUrl] = useState("");
  const [directLoading, setDirectLoading] = useState(false);
  const [directError, setDirectError] = useState(false);
  const [textContent, setTextContent] = useState("");
  const [textTruncated, setTextTruncated] = useState(false);
  const [textTooLarge, setTextTooLarge] = useState(false);
  const [textLoading, setTextLoading] = useState(false);
  const [textError, setTextError] = useState(false);
  const [metaPayload, setMetaPayload] = useState(null);
  const [metaLoading, setMetaLoading] = useState(false);
  const [metaError, setMetaError] = useState(false);
  const [previewErrorMessage, setPreviewErrorMessage] = useState("");
  const [footerAction, setFooterAction] = useState(null);
  const footerBusy = footerAction != null;
  const blobUrlRef = useRef("");
  const fileBlobRef = useRef(null);

  const useMeta = shouldUsePreviewMeta({ previewModule, fileId });
  const previewMetaUrl = useMemo(() => {
    if (!useMeta) return "";
    return buildPreviewMetaUrl({
      module: previewModule,
      fileId,
      projektTyp,
      projektId,
      entityId,
    });
  }, [useMeta, previewModule, fileId, projektTyp, projektId, entityId]);

  const metaUrls = useMemo(() => {
    if (!metaPayload) return { preview: "", download: "" };
    return {
      preview: sanitizeFileUrl(metaPayload.previewUrl || ""),
      download: sanitizeFileUrl(metaPayload.downloadUrl || ""),
    };
  }, [metaPayload]);

  const safeFileUrl = sanitizeFileUrl(metaUrls.preview || fileUrl);
  const safeDownloadUrl = sanitizeFileUrl(downloadUrl || metaUrls.download || fileUrl);

  const displayUrl =
    resolvedUrl || (safeFileUrl && !safeFileUrl.startsWith("/api/") ? safeFileUrl : "");

  // API-/Download-URL vor Blob: sonst „Im Tab öffnen“ → UUID-Download ohne Endung.
  const openTabUrl = safeFileUrl || safeDownloadUrl || displayUrl;
  const downloadTargetUrl = safeDownloadUrl || safeFileUrl;
  const shareTargetUrl = downloadTargetUrl || openTabUrl;

  const ext = useMemo(() => {
    const explicit = normalizeType(fileType);
    if (explicit) return explicit;
    return getFileExtension(fileName || safeFileUrl);
  }, [fileType, fileName, safeFileUrl]);

  const detectedType = getFileType(ext);
  const isImage = isImageFile(ext);
  const isPdf = isPdfFile(ext);
  const isOfficeLike = ["doc", "docx", "ppt", "pptx", "odt", "ods", "odp"].includes(ext);
  const isArchive = ["zip", "rar", "7z"].includes(ext);
  const isExportPdf = isExportPdfUrl(safeFileUrl);
  const isMobileLike = preferPdfExportActionSheet();
  const legacyStrategy = useMemo(
    () => getPreviewStrategy(ext, { isMobile: isMobileLike }),
    [ext, isMobileLike],
  );

  const activeType = metaPayload?.type || null;
  const metaPreviewFailed = Boolean(useMeta && metaError && !metaLoading);
  const activeStrategy = useMemo(() => {
    if (useMeta && metaLoading) return "loading";
    if (useMeta && metaPayload?.type) {
      if (metaPayload.type === "direct") return "direct";
      if (metaPayload.type === "text") return "text";
      if (metaPayload.type === "email") return "email";
      if (metaPayload.type === "unsupported") return "unsupported";
    }
    if (metaPreviewFailed) return "unsupported";
    return legacyStrategy;
  }, [useMeta, metaLoading, metaPayload, metaPreviewFailed, legacyStrategy]);

  const canInlinePdf = Boolean(
    activeStrategy === "direct" && isPdf && displayUrl && !pdfFailed,
  );
  const canInlineImage = Boolean(activeStrategy === "direct" && isImage && displayUrl && !imgFailed);

  const metaTextContent = activeType === "text" ? String(metaPayload?.content || "") : "";
  const isSpreadsheetPreview = Boolean(metaPayload?.previewKind === "spreadsheet");
  const showMetaText = Boolean(activeType === "text" && !metaLoading);
  const textPreviewReady = Boolean(
    legacyStrategy === "text" && !textLoading && !textError && !textTooLarge,
  );
  const showLegacyText = Boolean(!useMeta && activeStrategy === "text" && textPreviewReady);
  const showTextPreview = showMetaText || showLegacyText;
  const displayedText = showMetaText ? metaTextContent : textContent;
  const displayedTextTruncated = showMetaText
    ? Boolean(metaPayload?.truncated)
    : textTruncated;

  const showEmailPreview = Boolean(
    activeType === "email" && !metaLoading && !metaError && !metaPreviewFailed,
  );

  const pdfBlobPending = Boolean(
    activeStrategy === "direct" &&
      isPdf &&
      safeFileUrl &&
      !displayUrl &&
      !directError &&
      !pdfFailed,
  );

  const isLoading = Boolean(
    activeStrategy === "loading" ||
      (activeStrategy === "direct" && directLoading) ||
      pdfBlobPending ||
      (!useMeta && legacyStrategy === "text" && textLoading),
  );

  const hasLoadError = Boolean(
    metaPreviewFailed ||
      directError ||
      (!useMeta && legacyStrategy === "text" && textError),
  );

  const unsupportedReason =
    activeType === "unsupported" ? String(metaPayload?.reason || "").trim() : "";

  const fileSizeBytes = metaPayload?.fileSize ?? null;

  const modalTitle = title || (fileName ? `Vorschau: ${fileName}` : "Dateivorschau");

  const shareFileLabel = fileName || "datei";
  const nativeShareAvailable = useMemo(
    () =>
      Boolean(
        canShare &&
          shouldOfferNativeFileShare(shareFileLabel, {
            extHint: ext,
            preferMobile: isMobileLike,
          }),
      ),
    [canShare, shareFileLabel, ext, isMobileLike],
  );
  const showPrintButton = Boolean(canPrint && isPdf && (displayUrl || downloadTargetUrl));
  const showShareButton = Boolean(nativeShareAvailable && shareTargetUrl);

  const notifyFooterError = (message) => {
    if (!message) return;
    setPreviewErrorMessage(message);
    showError(message);
  };

  const fallbackMessage = useMemo(() => {
    if (!safeFileUrl && !safeDownloadUrl) {
      return "Datei-URL ist ungültig oder aus Sicherheitsgründen blockiert.";
    }
    if (metaPreviewFailed) {
      return "Die strukturierte Vorschau konnte nicht geladen werden. Du kannst die Datei weiterhin öffnen oder herunterladen.";
    }
    if (previewErrorMessage) return previewErrorMessage;
    if (hasLoadError) {
      return "Vorschau konnte nicht geladen werden. Bitte «Im Tab öffnen» oder «Download» verwenden.";
    }
    if (textTooLarge) {
      return `Die Textdatei ist grösser als ${formatMaxPreviewSize()} und kann nicht in der Vorschau angezeigt werden.`;
    }
    if (unsupportedReason) return unsupportedReason;
    if (isLoading) {
      return isExportPdf
        ? "PDF wird erstellt – bei grossen Berichten kann das einige Sekunden dauern…"
        : "Vorschau wird geladen…";
    }
    if (pdfFailed) {
      return "Die PDF-Vorschau wurde vom Browser blockiert. Bitte «Im Tab öffnen» oder «Download» verwenden.";
    }
    if (imgFailed) {
      return `Keine direkte Vorschau (${detectedType || ext || "Datei"}). Bitte «Im Tab öffnen» oder «Download» verwenden.`;
    }
    return "Für diesen Dateityp ist keine direkte Vorschau verfügbar.";
  }, [
    safeFileUrl,
    safeDownloadUrl,
    metaPreviewFailed,
    hasLoadError,
    textTooLarge,
    unsupportedReason,
    isLoading,
    imgFailed,
    pdfFailed,
    detectedType,
    ext,
    previewErrorMessage,
    isExportPdf,
  ]);

  const unsupportedHints = useMemo(() => {
    const hints = [];
    if (isPdf && (pdfFailed || !canInlinePdf) && openTabUrl) {
      hints.push("Alternativ: «Im Tab öffnen» für die System-PDF-Ansicht.");
    }
    if (isOfficeLike) {
      hints.push(
        "Office-Dateien können im Browser nicht eingebettet werden. «Im Tab öffnen» startet ggf. die passende App.",
      );
    }
    if (isArchive) {
      hints.push("Archive werden aus Sicherheitsgründen nicht entpackt.");
    }
    return hints;
  }, [isPdf, isOfficeLike, isArchive, pdfFailed, canInlinePdf, openTabUrl]);

  const showUnsupportedPanel = Boolean(
    !isLoading &&
      !canInlineImage &&
      !canInlinePdf &&
      !showTextPreview &&
      !showEmailPreview &&
      (!openTabUrl ||
        activeStrategy === "unsupported" ||
        hasLoadError ||
        textTooLarge ||
        (activeStrategy === "direct" && (imgFailed || pdfFailed))),
  );

  useEffect(() => {
    setImgFailed(false);
    setPdfFailed(false);
    setDirectError(false);
    setTextError(false);
    setTextTooLarge(false);
    setTextTruncated(false);
    setMetaError(false);
    setPreviewErrorMessage("");
  }, [open, fileUrl, fileName, fileType, previewModule, fileId]);

  useEffect(() => {
    if (!open || !useMeta || !previewMetaUrl) {
      setMetaPayload(null);
      setMetaLoading(false);
      return undefined;
    }

    const ctrl = new AbortController();
    let alive = true;
    setMetaLoading(true);
    setMetaError(false);
    setMetaPayload(null);

    fetchPreviewMeta(previewMetaUrl, { signal: ctrl.signal })
      .then((data) => {
        if (!alive) return;
        setMetaPayload(data);
      })
      .catch((err) => {
        if (!alive || err?.name === "AbortError") return;
        setMetaError(true);
      })
      .finally(() => {
        if (alive) setMetaLoading(false);
      });

    return () => {
      alive = false;
      ctrl.abort();
    };
  }, [open, useMeta, previewMetaUrl]);

  useEffect(() => {
    const clearBlob = () => {
      revokeAuthenticatedPreviewUrl(blobUrlRef.current);
      blobUrlRef.current = "";
      fileBlobRef.current = null;
    };

    const wantsDirect =
      activeStrategy === "direct" || (metaPayload?.type === "direct" && !metaLoading);

    if (!open || !wantsDirect) {
      clearBlob();
      setResolvedUrl("");
      setDirectLoading(false);
      setDirectError(false);
      return undefined;
    }

    if (!safeFileUrl) {
      clearBlob();
      setResolvedUrl("");
      return undefined;
    }

    if (!safeFileUrl.startsWith("/api/") && !safeFileUrl.startsWith("/export/")) {
      clearBlob();
      setResolvedUrl(safeFileUrl);
      return undefined;
    }

    let alive = true;
    setDirectLoading(true);
    setDirectError(false);
    setPreviewErrorMessage("");
    fetchProtectedPreviewAssets(safeFileUrl)
      .then((assets) => {
        if (!alive) return;
        clearBlob();
        blobUrlRef.current = assets?.blobUrl || "";
        fileBlobRef.current = assets?.blob || null;
        setResolvedUrl(assets?.blobUrl || "");
        if (!assets?.blobUrl) setDirectError(true);
      })
      .catch((err) => {
        if (!alive) return;
        clearBlob();
        setResolvedUrl("");
        setDirectError(true);
        if (err instanceof PdfPreviewError) {
          setPreviewErrorMessage(err.message);
        }
      })
      .finally(() => {
        if (alive) setDirectLoading(false);
      });

    return () => {
      alive = false;
      clearBlob();
    };
  }, [open, safeFileUrl, activeStrategy, metaPayload?.type, metaLoading]);

  useEffect(() => {
    if (!open || useMeta || legacyStrategy !== "text") {
      setTextContent("");
      setTextLoading(false);
      setTextError(false);
      setTextTooLarge(false);
      setTextTruncated(false);
      return undefined;
    }

    if (!safeFileUrl) {
      setTextContent("");
      return undefined;
    }

    const ctrl = new AbortController();
    let alive = true;

    setTextLoading(true);
    setTextError(false);
    setTextTooLarge(false);
    setTextTruncated(false);
    setTextContent("");

    fetchAuthenticatedPreviewText(safeFileUrl, {
      maxBytes: MAX_TEXT_PREVIEW_BYTES,
      signal: ctrl.signal,
    })
      .then((result) => {
        if (!alive) return;
        if (!result.ok) {
          if (result.error === "TOO_LARGE") {
            setTextTooLarge(true);
            return;
          }
          setTextError(true);
          return;
        }
        setTextContent(result.text || "");
        setTextTruncated(Boolean(result.truncated));
      })
      .catch((err) => {
        if (!alive || err?.name === "AbortError") return;
        setTextError(true);
      })
      .finally(() => {
        if (alive) setTextLoading(false);
      });

    return () => {
      alive = false;
      ctrl.abort();
    };
  }, [open, safeFileUrl, legacyStrategy, useMeta]);

  const handleOpenInNewTab = async () => {
    const target = openTabUrl;
    if (!target || footerBusy) return;
    setFooterAction("open");
    try {
      const opened = await openProtectedFileInNewTab(target, {
        existingBlobUrl: displayUrl,
      });
      if (!opened) {
        notifyFooterError(
          "Der Browser hat das neue Fenster blockiert. Bitte Pop-ups für diese Seite erlauben oder «Download» nutzen.",
        );
      }
    } catch {
      notifyFooterError("Datei konnte nicht geöffnet werden.");
    } finally {
      setFooterAction(null);
    }
  };

  const handleDownload = async () => {
    const target = downloadTargetUrl;
    if (!target || footerBusy) return;
    setFooterAction("download");
    try {
      const ok = await downloadProtectedFile(target, fileName || "datei", {
        existingBlobUrl: displayUrl,
      });
      if (!ok) notifyFooterError("Download konnte nicht gestartet werden.");
    } catch {
      notifyFooterError("Download konnte nicht gestartet werden.");
    } finally {
      setFooterAction(null);
    }
  };

  const handlePrint = async () => {
    const target = downloadTargetUrl || openTabUrl;
    if (!target || footerBusy) return;
    setFooterAction("print");
    setPreviewErrorMessage("");
    try {
      const ok = await printProtectedFile(target, { existingBlobUrl: displayUrl });
      if (!ok) notifyFooterError("Drucken konnte nicht gestartet werden.");
    } catch {
      notifyFooterError("Drucken konnte nicht gestartet werden.");
    } finally {
      setFooterAction(null);
    }
  };

  const handleShare = async () => {
    const target = shareTargetUrl;
    if (!target || footerBusy) return;
    setFooterAction("share");
    setPreviewErrorMessage("");
    try {
      const result = await shareProtectedFile(target, fileName || "datei", {
        existingBlobUrl: displayUrl,
        existingBlob: fileBlobRef.current,
        shareTitle: title || fileName || "",
      });
      if (!result.ok && result.reason !== "cancelled") {
        const msg =
          result.reason === "unsupported"
            ? "Teilen wird auf diesem Gerät nicht unterstützt – nutze «Download» oder «Im Tab öffnen»."
            : result.reason === "fetch"
              ? "Datei konnte nicht für Teilen vorbereitet werden."
              : "Teilen fehlgeschlagen.";
        notifyFooterError(msg);
      }
    } catch {
      notifyFooterError("Teilen fehlgeschlagen.");
    } finally {
      setFooterAction(null);
    }
  };

  const canSaveSharepoint =
    sharepointReady &&
    Boolean(previewModule) &&
    Boolean(fileId) &&
    ["service_files", "offer_files", "project_files"].includes(String(previewModule));

  const handleSaveToSharePoint = async () => {
    if (!canSaveSharepoint || footerBusy) return;
    setFooterAction("sharepoint");
    try {
      const { data } = await api.post(
        "/sharepoint/files/push",
        {
          module: previewModule,
          fileId: Number(fileId),
          projektTyp: projektTyp || undefined,
          projektId: projektId || undefined,
          entityId: entityId || undefined,
        },
        { silent: true }
      );
      if (!data?.ok) {
        showError(data?.detail || data?.error || "Speichern in SharePoint fehlgeschlagen.");
        return;
      }
      if (data.already) {
        showInfo("Datei ist bereits in SharePoint.");
      } else {
        showSuccess("In SharePoint gespeichert.");
      }
      if (data.web_url) {
        window.open(data.web_url, "_blank", "noopener,noreferrer");
      }
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        e?.message ||
        "Speichern in SharePoint fehlgeschlagen.";
      showError(typeof msg === "string" ? msg : "Speichern in SharePoint fehlgeschlagen.");
    } finally {
      setFooterAction(null);
    }
  };

  const actionFooter = (
    <div
      className="flex flex-col-reverse sm:flex-row sm:flex-wrap items-stretch sm:items-center justify-end gap-2 w-full"
      data-testid="file-preview-footer"
    >
      {showPrintButton ? (
        <AppButton
          type="button"
          variant="secondary"
          size="sm"
          onClick={handlePrint}
          disabled={footerBusy || directLoading}
          className="w-full sm:w-auto min-h-[44px]"
          aria-label={`PDF drucken: ${fileName || "Datei"}`}
          data-testid="file-preview-print"
        >
          {footerAction === "print" ? "Druckt…" : "Drucken"}
        </AppButton>
      ) : null}
      {canOpenInNewTab && openTabUrl ? (
        <AppButton
          type="button"
          variant="secondary"
          size="sm"
          onClick={handleOpenInNewTab}
          disabled={footerBusy || directLoading}
          className="w-full sm:w-auto min-h-[44px]"
          aria-label={`Datei im neuen Tab öffnen: ${fileName || "Datei"}`}
        >
          {footerAction === "open" ? "Öffnet…" : "Im Tab öffnen"}
        </AppButton>
      ) : null}
      {canDownload && downloadTargetUrl ? (
        <AppButton
          type="button"
          variant="secondary"
          size="sm"
          onClick={handleDownload}
          disabled={footerBusy || directLoading}
          className="w-full sm:w-auto min-h-[44px]"
          aria-label={`Datei herunterladen: ${fileName || "Datei"}`}
        >
          {footerAction === "download" ? "Lädt…" : "Download"}
        </AppButton>
      ) : null}
      {canSaveSharepoint ? (
        <AppButton
          type="button"
          variant="secondary"
          size="sm"
          onClick={handleSaveToSharePoint}
          disabled={footerBusy || directLoading}
          className="w-full sm:w-auto min-h-[44px]"
          aria-label={`In SharePoint speichern: ${fileName || "Datei"}`}
          data-testid="file-preview-sharepoint-save"
        >
          {footerAction === "sharepoint" ? "Speichert…" : "In SharePoint speichern"}
        </AppButton>
      ) : null}
      {showShareButton ? (
        <AppButton
          type="button"
          variant="primary"
          size="sm"
          onClick={handleShare}
          disabled={footerBusy || directLoading}
          className="w-full sm:w-auto min-h-[44px]"
          aria-label={`Datei teilen: ${fileName || "Datei"}`}
          data-testid="file-preview-share"
        >
          {footerAction === "share" ? "Teilt…" : "Teilen"}
        </AppButton>
      ) : null}
    </div>
  );

  const hasFooterActions = Boolean(
    showPrintButton ||
      showShareButton ||
      canSaveSharepoint ||
      (canOpenInNewTab && openTabUrl) ||
      (canDownload && downloadTargetUrl),
  );

  return (
    <ModalOrSheet
      open={open}
      onClose={onClose}
      title={modalTitle}
      maxWidth="xl"
      footer={hasFooterActions ? actionFooter : null}
    >
      <div className="space-y-3 min-w-0">
        {description ? <p className="text-sm text-blue-200/80">{description}</p> : null}

        {previewErrorMessage && (canInlinePdf || canInlineImage) ? (
          <p className="text-sm text-amber-200/90" role="alert">
            {previewErrorMessage}
          </p>
        ) : null}

        {isLoading ? (
          <div
            className="rounded-xl border border-white/10 bg-white/5 p-4 animate-pulse"
            data-testid="file-preview-loading"
          >
            <p className="text-sm text-blue-200/80">
              {isExportPdf
                ? "PDF wird erstellt – bei grossen Berichten kann das einige Sekunden dauern…"
                : "Vorschau wird geladen…"}
            </p>
          </div>
        ) : null}

        {canInlineImage ? (
          <div className="rounded-xl border border-white/10 bg-black/30 p-2 md:p-3 min-w-0">
            <div className="w-full flex items-center justify-center min-h-[160px] max-h-[min(65dvh,520px)] overflow-hidden">
              <img
                src={displayUrl}
                alt={fileName || "Bildvorschau"}
                className="max-h-[min(62dvh,500px)] w-auto max-w-full object-contain"
                loading="lazy"
                onError={() => setImgFailed(true)}
              />
            </div>
          </div>
        ) : null}

        {canInlinePdf ? (
          <div
            className="pdf-embed-shell rounded-xl border border-white/10 bg-[#0b1324] overflow-hidden min-w-0"
            data-testid="pdf-embed-shell"
          >
            <PdfInlinePreview
              src={displayUrl}
              title={fileName || "PDF-Vorschau"}
              preferEmbed={isMobileLike}
              onFailed={() => setPdfFailed(true)}
            />
          </div>
        ) : null}

        {showEmailPreview ? (
          <EmailPreviewBody
            subject={metaPayload?.subject}
            from={metaPayload?.from}
            to={metaPayload?.to}
            cc={metaPayload?.cc}
            date={metaPayload?.date}
            text={metaPayload?.text}
            truncated={metaPayload?.truncated}
            attachments={metaPayload?.attachments}
            previewModule={previewModule}
            fileId={fileId}
            projektTyp={projektTyp}
            projektId={projektId}
            entityId={entityId}
          />
        ) : null}

        {showTextPreview ? (
          <TextPreviewBody
            content={displayedText}
            truncated={displayedTextTruncated}
            variant={showMetaText && isSpreadsheetPreview ? "spreadsheet" : "text"}
          />
        ) : null}

        {showUnsupportedPanel ? (
          <UnsupportedPreviewPanel
            fileName={fileName || metaPayload?.filename}
            ext={ext}
            fileSize={fileSizeBytes}
            message={fallbackMessage}
            hints={unsupportedHints}
          />
        ) : null}

        {!isLoading &&
        !showUnsupportedPanel &&
        !canInlineImage &&
        !canInlinePdf &&
        !showTextPreview &&
        !showEmailPreview &&
        openTabUrl ? (
          <p className="text-xs text-blue-200/60">
            Keine eingebettete Vorschau – nutze die Buttons unten.
          </p>
        ) : null}
      </div>
    </ModalOrSheet>
  );
}
